import * as React from "react";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import AddModuleForm from "./add-module-form";
import { LibraryItem, LibrarySourceMode, LibraryView, PackageItem, PackageVersion } from "./external-libraries.types";
import InstalledLibrariesList from "./installed-libraries-list";
import PackageList from "./package-list";
import { ExternalLibrariesController } from "./use-external-libraries";

interface LibraryTabOption {
  value: LibraryView;
  label: string;
  /** Stable hook for the E2E suite; KuikaTabs renders it on a span wrapping the label. */
  dataTestId: string;
}

interface ExternalLibrariesPopoverProps {
  controller: ExternalLibrariesController;
  libraries: LibraryItem[];
  packages: PackageItem[];
  versions: PackageVersion[];
  hasMorePackages: boolean;
  sourceMode: LibrarySourceMode;
  modulePlaceholder?: string;
  moduleHelpText?: string;
  moduleExamples?: string[];
}

const ExternalLibrariesPopover: React.FC<ExternalLibrariesPopoverProps> = ({
  controller,
  libraries,
  packages,
  versions,
  hasMorePackages,
  sourceMode,
  modulePlaceholder,
  moduleHelpText,
  moduleExamples
}) => {
  const isModule: boolean = sourceMode === "module";
  const isAddTab: boolean = controller.view === "new";

  const tabOptions: LibraryTabOption[] = [
    { value: "installed", label: "Installed", dataTestId: "externalLibrariesInstalledTab" },
    { value: "new", label: isModule ? "Add" : "Browse", dataTestId: "externalLibrariesAddTab" }
  ];

  const query: string = controller.search.trim().toLowerCase();
  const filteredLibraries: LibraryItem[] = libraries.filter((library) => library.name.toLowerCase().includes(query));

  // Module mode's "add" tab carries its own input, so the shared search box is hidden there.
  const showSearch: boolean = !(isModule && isAddTab);

  // The pane for one tab value. Drawn from its own key (not `controller.view`), the way
  // `KuikaTabContent`'s contract requires, so the leaving pane while the tabs swap is still the
  // pane it was.
  const renderPane = (paneView: LibraryView): React.ReactNode => {
    if (paneView === "installed") {
      return (
        <InstalledLibrariesList
          libraries={filteredLibraries}
          loading={controller.loadingLibraries}
          search={controller.search}
          expanded={controller.expanded}
          expandable={!isModule}
          isModule={isModule}
          versions={versions}
          loadingVersions={controller.loadingVersions}
          onExpand={controller.toggleExpanded}
          onDelete={controller.remove}
          onSelectVersion={controller.selectVersion}
          onBrowse={() => controller.setView("new")}
        />
      );
    }
    if (isModule) {
      return (
        <AddModuleForm
          installed={libraries}
          adding={controller.addingId !== undefined}
          placeholder={modulePlaceholder}
          helpText={moduleHelpText}
          examples={moduleExamples}
          onAdd={controller.add}
        />
      );
    }
    return (
      <PackageList
        packages={packages}
        loading={controller.loadingPackages}
        loadingMore={controller.loadingMore}
        search={controller.search}
        installed={libraries}
        addingId={controller.addingId}
        hasMore={hasMorePackages}
        onAdd={controller.add}
        onLoadMore={controller.loadMore}
      />
    );
  };

  return (
    <div className="kuika-external-libraries" data-testid="externalLibrariesPopover">
      <div className="kuika-external-libraries__toolbar">
        <KuikaTabs
          size="sm"
          value={controller.view}
          options={tabOptions}
          onChange={(value: string) => controller.setView(value as LibraryView)}
        />
        {showSearch && (
          <KuikaInput
            size="sm"
            data-testid="externalLibrariesSearchInput"
            className="kuika-external-libraries__search"
            placeholder={isAddTab ? "Search packages" : "Search installed libraries"}
            value={controller.search}
            leftIcon={<KuikaSvg name="search" width={16} height={16} />}
            onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
              controller.setSearch(String(value))
            }
          />
        )}
      </div>

      <div className="kuika-external-libraries__list">
        {/* Installed and Add/Browse swap under their tab; each pane is drawn from its own key, so
            the leaving one is still the pane it was. */}
        <KuikaTabContent
          activeKey={controller.view}
          order={tabOptions.map((option: LibraryTabOption): string => option.value)}
          render={(paneView: string): React.ReactNode => renderPane(paneView as LibraryView)}
        />
      </div>
    </div>
  );
};

export default ExternalLibrariesPopover;
